import { createSelector } from 'reselect';
import { filter, maxBy, find, uniq, flatten } from 'lodash';
import { MODELS } from '../constants/admin/settings';
import { currentPath } from './router';
import { PRIVACY_PATH, TCO_PATH } from '../constants/routes';
import { relatedToFromFunction } from './admin/models/permissions';
import {
    PERMISSIONS,
    ROLE_ORGANIZATION_USER,
    ROLE_SUPER_ORGANIZATION_USER
} from '../constants/authentication/permissions';

const authentication = state => state.authentication;
const form = state => state.authentication.form;
export const user = state => state.authentication.user;
export const userOrgContext = state => state.authentication.user.preferences.context;
const events = state => state.events.list.items || [];

export const authenticatedSelector = createSelector(authentication, auth => auth.authenticated);
export const userEmailSelector = createSelector(user, u => u.email);
export const notificationsSeenSelector = createSelector(user, u => u.notificationsSeenAt);
export const preferencesSelector = createSelector(user, u => u.preferences || {});
export const favoriteArtistsSelector = createSelector(user, u => (u && u.favoriteArtists) || []);

export const userIdSelector = createSelector(user, u => u.id);
export const userOrganizationsSelector = createSelector(user, u =>
    u.organizations.map(o => {
        o.id = o.relatedToId;
        return o;
    })
);
export const currentUserSelector = createSelector(user, u => u);
export const hasAgreedTosSelector = createSelector(user, currentPath, (u, path) =>
    u.fetched ? (path === PRIVACY_PATH || path === TCO_PATH ? true : u.tosAndPrivacy) : true
);

export const rememberMeSelector = createSelector(form, f => f.rememberMe);
export const emailSelector = createSelector(form, f => f.email);
export const passwordSelector = createSelector(form, f => f.password);
export const isSubmittingSelector = createSelector(form, f => f.submitting);
export const userFetchedSelector = createSelector(user, u => u.fetched);
export const errorSelector = createSelector(form, f => f.error);

export const oldPasswordSelector = createSelector(form, f => f.oldPassword);
export const newPasswordSelector = createSelector(form, f => f.newPassword);
export const newPasswordConfirmationSelector = createSelector(form, f => f.newPasswordConfirmation);

export const canSubmitForgotPassword = createSelector(form, f => f.email && !f.submitting);
export const canSubmitChangePassword = createSelector(
    form,
    f =>
        f.newPassword &&
        f.oldPassword &&
        f.newPasswordConfirmation &&
        f.newPassword === f.newPasswordConfirmation &&
        f.newPassword.length > 4 &&
        f.newPasswordConfirmation.length > 4 &&
        !f.submitting
);

export const newPasswordError = createSelector(form, f => f.newPassword && f.newPassword.length < 5);
export const newPasswordConfirmationError = createSelector(
    form,
    f => f.newPasswordConfirmation && f.newPassword !== f.newPasswordConfirmation
);

export const highestRoleSelector = (context, roles = []) => {
    const god = find(roles, r => (r.permissions || []).includes(PERMISSIONS.GOD));
    const highestViewAll = find(roles, r => (r.permissions || []).includes(PERMISSIONS.ORGANIZATIONS_VIEW_ALL));
    const highest = maxBy(
        filter(roles, r => relatedToFromFunction(r.function) === MODELS.ORGANIZATION && r.orgId === context),
        'level'
    );
    return (
        god ||
        highest ||
        highestViewAll || {
            level: -1,
            permissions: []
        }
    );
};

export const userOrgContextSelector = createSelector(user, u => {
    return u.preferences || {};
});

export const canChangePerformanceAccess = (user, orgId) => {
    const godUser = find(user.roles || [], r => (r.permissions || []).includes(PERMISSIONS.GOD));
    const hasAccessToPerformance = (user.roles || []).some(r => {
        return r.name === ROLE_ORGANIZATION_USER || r.name === ROLE_SUPER_ORGANIZATION_USER
            ? orgId && r.orgId === orgId && !!r.accessToPerformanceContracts
            : false;
    });
    return !!(orgId && (godUser || hasAccessToPerformance));
};

export const canAccessPerformanceInAdminSelector = createSelector(user, events, (user, events) => {
    const godUser = find(user.roles || [], r => (r.permissions || []).includes(PERMISSIONS.GOD));
    const hasAccessToPerformance = (user.roles || []).some(r => {
        return r.name === ROLE_ORGANIZATION_USER || r.name === ROLE_SUPER_ORGANIZATION_USER
            ? (r.orgId === user.contextRole.orgId || r.orgId === user.preferences.context) &&
                  !!r.accessToPerformanceContracts
            : !!r.accessToPerformanceContracts;
    });

    const eventsHaveAccessToPerformance = !!events.find(el => {
        return el.allowPerformanceContracts;
    });

    if (!!godUser) {
        return eventsHaveAccessToPerformance;
    }

    return hasAccessToPerformance && eventsHaveAccessToPerformance;
});

export const canAccessPerformanceInInbox = createSelector(user, user => {
    const godUser = find(user.roles || [], r => (r.permissions || []).includes(PERMISSIONS.GOD));
    const hasAccessToPerformance = (user.roles || []).some(r => {
        return !!r.accessToPerformanceContracts;
    });
    return godUser || hasAccessToPerformance;
});

export const userApiDataToState = user => {
    return {
        tosAndPrivacy: user.tosAndPrivacy,
        roles: user.roles || [],
        permissions: uniq(flatten([...(user.roles || []).map(r => r.permissions)])),
        email: user.email,
        favoriteArtists: user.favoriteArtists || [],
        id: user.id,
        fullName: user.fullName,
        notificationsSeenAt: user.notificationsSeenAt,
        preferences: user.preferences || {}
    };
};



// WEBPACK FOOTER //
// ./src/selectors/authentication.js